import clsx from 'clsx';
import React, { useId } from 'react';

/**
 * ChoiceCards: a small, mutually exclusive set where each option needs a
 * sentence of its own — "Start with a blank form" vs "Copy the setup of
 * an existing location".
 * Each option is a bordered card: radio · title · description, and the
 * selected card can open a follow-up (`reveal`) under its text.
 *
 * Boundaries: a set of one-word options is `Radio` rows or `Segmented`;
 * plans with pricing are `PlanChooser`. The cards are native radios, so
 * arrow keys and form semantics come free.
 */
export interface Choice<V extends string = string> {
  value: V;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Under the selected card: the follow-up it needs (a select, a field). */
  reveal?: React.ReactNode;
}

export function ChoiceCards<V extends string = string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: {
  options: Choice<V>[];
  value: V | null;
  onChange: (value: V) => void;
  className?: string;
  'aria-label': string;
}) {
  const name = useId();
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={clsx('flex flex-col gap-2', className)}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          // The follow-up sits inside the selected card's border, so the
          // cards stay two alternatives rather than a form between them.
          <div
            key={o.value}
            className={clsx(
              'rounded-v2-lg border transition-colors duration-100',
              selected
                ? 'border-v2-accent bg-v2-accent/[0.04]'
                : 'border-v2-line-strong/80 bg-v2-surface hover:border-v2-line-strong',
            )}
          >
            <label className="flex cursor-pointer items-start gap-3 px-3.5 py-3">
              <input
                type="radio"
                name={name}
                checked={selected}
                onChange={() => onChange(o.value)}
                className="mt-0.5 h-4 w-4 shrink-0 border-v2-line-strong bg-v2-surface text-v2-accent focus:ring-2 focus:ring-v2-accent/25 focus:ring-offset-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-v2-13 font-semibold leading-4 text-v2-fg">
                  {o.title}
                </span>
                {o.description && (
                  <span className="mt-1 block text-v2-12 leading-[18px] text-v2-fg-3">
                    {o.description}
                  </span>
                )}
              </span>
            </label>
            {selected && o.reveal && (
              <div className="border-t border-v2-accent/20 py-3 pl-[42px] pr-3.5">
                {o.reveal}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
